import { PropsWithChildren, Suspense, useState } from "react"
import * as yup from "yup"
import { Form, Formik } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box, Grid, Step, StepLabel, Stepper } from "@mui/material"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { BeginOnboardingFormValues, BeginOnboardingType } from "../../supplierMerchantsTypes"
import { QueryErrorBoundary } from "../../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../../shared/components/loading/LoadingSpinner"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { merchantBeginOnboarding, SupplierMerchantQuery } from "../../supplierMerchantsApi"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../../core/api/errorTypes"
import { useMerchant } from "../../../../../../merchants/merchantHooks"
import { queryKeyFor } from "../../../../../../../core/api/useApiQuery"
import { SecondStep } from "./SecondStep"
import { FirstStep } from "./FirstStep"

type BeginOnboardingDialogProps = Readonly<
    PropsWithChildren<{
        open: boolean
        onClose: () => void
    }>
>

const useStyles = makeStyles()(() => ({
    container: {
        gap: "2rem",
    },
    content: {
        width: "100%",
        padding: "0 1.5rem 1rem",
    },

    stepper: {
        maxWidth: "25rem",
        margin: "0 auto",
    },
}))

export function BeginOnboardingDialog({ open, onClose }: BeginOnboardingDialogProps) {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const [activeStep, setActiveStep] = useState(0)

    const { mutate: callMerchantBeginOnboarding } = useApiMutation(merchantBeginOnboarding, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(SupplierMerchantQuery.DETAILS, { merchantId: params.merchantId }),
            })
            onClose()
        },
    })

    const handleSubmit = (values: BeginOnboardingFormValues) => {
        const modifyValues = {
            step: { type: BeginOnboardingType.BEGIN_ONBOARDING, onboardingDetails: values },
        }
        callMerchantBeginOnboarding({ merchantId, content: modifyValues })
    }

    const defaultInitialValues: BeginOnboardingFormValues = {
        articles: [],
        receipients: {
            administrators: [],
            extraReceivers: [],
        },
    }

    const validationSchema = yup.object().shape({
        receipients: yup.object().shape({
            extraReceivers: yup.array().of(
                yup.object().shape({
                    email: yup
                        .string()
                        .email(
                            l10n.getString(
                                "support-create-supplier-subscription-status-begin-onboarding-dialog-email-error",
                            ),
                        ),
                }),
            ),
            administrators: yup
                .array()
                .min(
                    1,
                    l10n.getString(
                        "support-create-supplier-subscription-status-begin-onboarding-dialog-administrators-error",
                    ),
                ),
        }),
    })
    const handleNext = () => {
        const newActiveStep = activeStep + 1
        setActiveStep(newActiveStep)
    }

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            dialogTitle={
                <LocalizedStrict id="support-create-supplier-subscription-status-begin-onboarding-dialog">
                    <>Begin onboarding</>
                </LocalizedStrict>
            }
            scroll="body"
        >
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                direction="column"
                wrap="nowrap"
                className={classes.container}
            >
                <Box className={classes.content}>
                    <Stepper activeStep={activeStep} nonLinear className={classes.stepper}>
                        <Step completed={true}>
                            <StepLabel>
                                <LocalizedStrict id="support-create-supplier-subscription-status-begin-onboarding-dialog-articles-to-send">
                                    <>Articles to send</>
                                </LocalizedStrict>
                            </StepLabel>
                        </Step>
                        <Step>
                            <StepLabel>
                                <LocalizedStrict id="support-create-supplier-subscription-status-begin-onboarding-dialog-destination">
                                    <>Destination</>
                                </LocalizedStrict>
                            </StepLabel>
                        </Step>
                    </Stepper>
                </Box>
                <Box className={classes.content}>
                    <Formik<BeginOnboardingFormValues>
                        enableReinitialize
                        initialValues={defaultInitialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        <Form>
                            {!activeStep ? (
                                <QueryErrorBoundary>
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <FirstStep handleNext={handleNext} />
                                    </Suspense>
                                </QueryErrorBoundary>
                            ) : (
                                <QueryErrorBoundary>
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <SecondStep />
                                    </Suspense>
                                </QueryErrorBoundary>
                            )}
                        </Form>
                    </Formik>
                </Box>
            </Grid>
        </StandardDialog>
    )
}

